import React, { useEffect, useMemo, useState } from 'react';
import Flatpickr from 'react-flatpickr';
import { Label, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { FormActions, FormGrid } from '../../../Components/Form';
import ActionButton from '../../../Components/Common/ActionButton';
import useAsyncAction from '../../../Components/Hooks/useAsyncAction';
import { formatDateTime } from '../../../utils/format';
import {
  todayWibDate,
  defaultDeadlineTime,
  jakartaInputToMs,
  pickerDateToWibBody,
  wibBodyToPickerDate,
} from '../utils/formatters';

const WIB_OFFSET_MS = 7 * 60 * 60 * 1000;
const pad = n => String(n).padStart(2, '0');

const formatPickerValue = date => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// Extend opens one hour after the current deadline, so the default is valid.
const defaultPick = minDeadline => {
  const floor = Date.parse(minDeadline);
  if (!Number.isFinite(floor))
    return wibBodyToPickerDate(todayWibDate(), defaultDeadlineTime());
  const wib = new Date(floor + 60 * 60 * 1000 + WIB_OFFSET_MS);
  return wibBodyToPickerDate(
    `${wib.getUTCFullYear()}-${pad(wib.getUTCMonth() + 1)}-${pad(wib.getUTCDate())}`,
    `${pad(wib.getUTCHours())}:${pad(wib.getUTCMinutes())}`
  );
};

// Start and Extend share this form. Only `{ date, time, tz }` is sent; the BE
// computes the deadline. `minDeadline` (Extend: the current deadline) and the
// server clock (`Date.now() + clockOffsetMs`, read at validate time: the page
// clock only ticks while a cargo runs) block a deadline not in the future.
const TimerSetupModal = ({
  isOpen,
  toggle,
  onConfirm,
  isSubmitting,
  isExtend = false,
  minDeadline = null,
  clockOffsetMs = 0,
}) => {
  const [picked, setPicked] = useState(() => defaultPick(minDeadline));
  const [minDate, setMinDate] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    const floorMs = Math.max(
      Date.now() + clockOffsetMs,
      Date.parse(minDeadline) || 0
    );
    const wib = new Date(floorMs + WIB_OFFSET_MS);
    setPicked(defaultPick(minDeadline));
    setMinDate(
      new Date(
        wib.getUTCFullYear(),
        wib.getUTCMonth(),
        wib.getUTCDate(),
        wib.getUTCHours(),
        wib.getUTCMinutes()
      )
    );
  }, [isOpen, minDeadline, clockOffsetMs]);

  const pickerOptions = useMemo(
    () => ({
      enableTime: true,
      time_24hr: true,
      enableSeconds: false,
      minuteIncrement: 1,
      dateFormat: 'd/m/Y H:i',
      allowInput: true,
      closeOnSelect: false,
      minDate: minDate || undefined,
    }),
    [minDate]
  );

  // Blocks a second submit (Enter key or double click) while onConfirm runs.
  const [confirm, isConfirming] = useAsyncAction(body => onConfirm(body));
  const isBusy = isSubmitting || isConfirming;

  const check = () => {
    const body = pickerDateToWibBody(picked);
    const floorMs = Math.max(
      Date.now() + clockOffsetMs,
      Date.parse(minDeadline) || 0
    );
    const pickedMs = body ? jakartaInputToMs(body.date, body.time) : NaN;
    const tooEarly = Number.isFinite(pickedMs) && pickedMs <= floorMs;
    const error = !body
      ? 'Target deadline is required.'
      : tooEarly
        ? isExtend
          ? `Pilih waktu setelah ${formatDateTime(minDeadline)} WIB.`
          : 'Pick a time in the future.'
        : undefined;
    return { body, error };
  };
  const { error } = check();

  const handleSubmit = e => {
    e.preventDefault();
    const { body, error: submitError } = check();
    if (submitError) return;
    confirm(body);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>
        {isExtend ? 'Extend deadline' : 'Start & set timer'}
      </ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalBody>
          <FormGrid columns={1}>
            <div>
              <Label for='targetDeadline' className='form-label fw-semibold'>
                Target deadline (WIB)
              </Label>
              <Flatpickr
                id='targetDeadline'
                className={`form-control${error ? ' is-invalid' : ''}`}
                value={formatPickerValue(picked)}
                options={pickerOptions}
                onChange={dates => {
                  const next = dates?.[0];
                  if (!(next instanceof Date) || Number.isNaN(next.getTime()))
                    return;
                  setPicked(next);
                }}
              />
              <div className='form-text'>
                {isExtend
                  ? `Batas waktu saat ini ${formatDateTime(minDeadline)} WIB. Pilih waktu setelah itu.`
                  : 'Jam dan menit saja, 24 jam. Contoh 27/10/2026 14:07.'}
              </div>
              {error && <div className='invalid-feedback d-block'>{error}</div>}
            </div>
          </FormGrid>
          <FormActions className='mt-3'>
            <ActionButton
              variant='secondary'
              icon='ri-close-line'
              type='button'
              onClick={toggle}
            >
              Cancel
            </ActionButton>
            <ActionButton
              variant='primary'
              icon={isExtend ? 'ri-time-line' : 'ri-play-circle-line'}
              type='submit'
              loading={isBusy}
              disabled={isBusy || Boolean(error)}
            >
              {isExtend ? 'Extend deadline' : 'Start negotiation'}
            </ActionButton>
          </FormActions>
        </ModalBody>
      </form>
    </Modal>
  );
};

export default TimerSetupModal;
